import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useAuth } from '../AuthContext';
import { updateProfile, getMenu, getPickupTimes, MenuItem, PickupPreference } from '../api';

function formatPhone(digits: string): string {
  if (digits.length <= 3) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function stripToDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [pizzaPreference, setPizzaPreference] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [pickupMode, setPickupMode] = useState<'anytime' | 'specific'>('anytime');
  const [preferredTime, setPreferredTime] = useState('');
  const [toleranceBefore, setToleranceBefore] = useState(10);
  const [toleranceAfter, setToleranceAfter] = useState(10);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [pickupSlots, setPickupSlots] = useState<string[]>([]);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (user) {
      setPhone(stripToDigits(user.phone || ''));
      setPizzaPreference(user.pizzaPreference || '');
      setQuantity(user.quantity || 1);
      setDisplayName(user.displayName || '');

      // Parse pickupWindow — handle both legacy string and structured object
      const pw = user.pickupWindow;
      if (pw && typeof pw === 'object' && 'mode' in pw) {
        const pref = pw as PickupPreference;
        setPickupMode(pref.mode);
        if (pref.preferredTime) setPreferredTime(pref.preferredTime);
        if (pref.toleranceBefore !== undefined) setToleranceBefore(pref.toleranceBefore);
        if (pref.toleranceAfter !== undefined) setToleranceAfter(pref.toleranceAfter);
      } else {
        setPickupMode('anytime');
      }
    }
  }, [user]);

  async function loadConfig() {
    setConfigLoading(true);
    const [menuRes, timesRes] = await Promise.all([
      getMenu(),
      getPickupTimes(),
    ]);
    if (menuRes.data) {
      setMenuItems(menuRes.data.items);
      // Set default pizza preference to first item if not set
      if (!user?.pizzaPreference && menuRes.data.items.length > 0) {
        setPizzaPreference(menuRes.data.items[0].name);
      }
    }
    if (timesRes.data) {
      setPickupSlots(timesRes.data.slots);
      if (!preferredTime && timesRes.data.slots.length > 0) {
        setPreferredTime(timesRes.data.slots[0]);
      }
    }
    setConfigLoading(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const pickupWindow: PickupPreference = pickupMode === 'anytime'
      ? { mode: 'anytime' }
      : { mode: 'specific', preferredTime, toleranceBefore, toleranceAfter };

    const { error: err } = await updateProfile({
      phone,
      pizzaPreference,
      quantity,
      pickupWindow,
      displayName,
    });
    setLoading(false);

    if (err) {
      setError(err);
    } else {
      setSuccess('Settings saved!');
      await refreshUser();
      setTimeout(() => setSuccess(''), 3000);
    }
  }

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 500, margin: '40px auto' }}>
        <h1>Settings</h1>

        {configLoading ? (
          <p>Loading...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label htmlFor="displayName">Display Name</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                value={formatPhone(phone)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const digits = stripToDigits(e.target.value);
                  if (digits.length > 0 && digits[0] === '1') return;
                  if (digits.length <= 10) setPhone(digits);
                }}
                placeholder="(206) 555-1234"
              />
              <small>Your mobile number — an SMS code will be sent during checkout</small>
            </div>

            <div className="field">
              <label htmlFor="pizza">Pizza Preference</label>
              <select
                id="pizza"
                value={pizzaPreference}
                onChange={e => setPizzaPreference(e.target.value)}
              >
                {menuItems.map(item => (
                  <option key={item.name} value={item.name}>
                    {item.name} (${item.price})
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="quantity">Quantity</label>
              <select
                id="quantity"
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
              >
                <option value={1}>1 pizza</option>
                <option value={2}>2 pizzas (max)</option>
              </select>
            </div>

            <div className="field">
              <label>Preferred Pickup Time</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="pickupMode"
                    value="anytime"
                    checked={pickupMode === 'anytime'}
                    onChange={() => setPickupMode('anytime')}
                  />
                  Anytime (first available)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal' }}>
                  <input
                    type="radio"
                    name="pickupMode"
                    value="specific"
                    checked={pickupMode === 'specific'}
                    onChange={() => setPickupMode('specific')}
                  />
                  Specific time
                </label>
              </div>

              {pickupMode === 'specific' && (
                <div style={{ marginTop: 8, paddingLeft: 24 }}>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label htmlFor="preferredTime">Preferred Time</label>
                    <select
                      id="preferredTime"
                      value={preferredTime}
                      onChange={e => setPreferredTime(e.target.value)}
                    >
                      {pickupSlots.map(slot => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div className="field" style={{ flex: 1 }}>
                      <label htmlFor="tolBefore">Flexibility before (min)</label>
                      <input
                        id="tolBefore"
                        type="number"
                        min={0}
                        value={toleranceBefore}
                        onChange={e => setToleranceBefore(Number(e.target.value))}
                      />
                    </div>
                    <div className="field" style={{ flex: 1 }}>
                      <label htmlFor="tolAfter">Flexibility after (min)</label>
                      <input
                        id="tolAfter"
                        type="number"
                        min={0}
                        value={toleranceAfter}
                        onChange={e => setToleranceAfter(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}

            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
